import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../util/store-types";
import classNames from "classnames";
import { useAvailabilityFilters } from "hooks/use-availability-filters";
import { displayModal } from "actions/shared-actions";
import { MODAL_NAMES } from "constants/modal-names";
import { isViewingAvailableOnly } from "helpers/grid-migration-helpers";
import RtrToggle from "components/design-system/rtr-toggle";
import Calendar from "images/design-system-svgs/calendar.svg";
import RentalDatePill from "./rental-dates-pill";
import { getRentalDateAnnotation } from "helpers/grid-top-filters-helpers";
import { isActiveMembership } from "helpers/membership-helpers";

export type GridFilterModalAvailabilitySectionProps = {
  onSubmitFilters: () => void;
};

const GridFilterModalAvailabilitySection = ({ onSubmitFilters }: GridFilterModalAvailabilitySectionProps) => {
  const isReserveGrid = useAppSelector(state => state.userData.isClassicLens && state.workingFilters.date);
  const { date, duration = "4" } = useAppSelector(state => state.workingFilters);
  const isAvailableOnly = useAppSelector(state => isViewingAvailableOnly(state.workingFilters, state.membershipState));
  const isComingSoonGrid = useAppSelector(state => state.gridMetaData?.isAvailabilityApplicable === false);
  const isActiveSubscriber = useAppSelector(state => isActiveMembership(state.membershipState));

  const [rentalType, setRentalType] = useState<"subscription" | "reserve">(isReserveGrid ? "reserve" : "subscription");

  const { setOrToggleMembershipAvailability } = useAvailabilityFilters(onSubmitFilters);
  const dispatch = useAppDispatch();
  const displayCalendarModal = () => dispatch(displayModal(MODAL_NAMES.CALENDAR_DRAWER));

  const onClickSubscription = () => {
    if (isReserveGrid) {
      setOrToggleMembershipAvailability();
    }

    setRentalType("subscription");
  };

  const onClickReserve = () => {
    setRentalType("reserve");
  };

  const pillContent = {
    non_subscriber: {
      reserve: { title: "Specific dates", annotation: "One-off rental", onClick: onClickReserve },
      subscription: { title: "Get it now", annotation: "With subscription", onClick: onClickSubscription },
    },
    subscriber: {
      reserve: { title: "One-off rental", annotation: "Reserve specific dates", onClick: onClickReserve },
      subscription: { title: "Subscription", annotation: "Add to your shipment", onClick: onClickSubscription },
    },
  } as const;

  const getPillContent = (type: "reserve" | "subscription") => {
    const userType = isActiveSubscriber ? "subscriber" : "non_subscriber";
    return pillContent[userType][type];
  };

  const renderRentalTypePill = (type: "reserve" | "subscription") => {
    const { title, annotation, onClick } = getPillContent(type);
    const classes = classNames("flex", "flex-col", "items-center", "gap-1", "w-1/2", "h-full", "justify-center", {
      "bg-white": type === rentalType,
      "rounded-xl": type === rentalType,
      "shadow-md": type === rentalType,
    });

    return (
      <button className={classes} onClick={onClick} data-test-id={`rental-type-pill-${type}`}>
        <div className="text-md">{title}</div>
        <div className="text-sm flex items-center gap-1">
          <span>{annotation}</span>
        </div>
      </button>
    );
  };
  const renderRentalTypePills = () => {
    return (
      <div className="flex justify-around items-center rounded-xl bg-offWhite h-16 p-1">
        {renderRentalTypePill("subscription")}
        {renderRentalTypePill("reserve")}
      </div>
    );
  };

  const availabilityToggle = (
    <RtrToggle
      checked={isAvailableOnly && !isComingSoonGrid}
      disabled={isComingSoonGrid}
      onChange={setOrToggleMembershipAvailability}
      dataTestId="subscription-availability-toggle"
    />
  );

  const filterContent = {
    dateSelected: {
      title: "Browse specific dates",
      annotation: `Items available to reserve ${getRentalDateAnnotation(date, duration)}`,
      ctaContent: <RentalDatePill onClearDates={setOrToggleMembershipAvailability} onClick={displayCalendarModal} />,
    },
    noDateSelected: {
      title: "Browse specific dates",
      annotation: isActiveSubscriber ? (
        <span className="text-tealGreen">Members get 25% off</span>
      ) : (
        "No subscription required"
      ),
      ctaContent: (
        <div>
          <button onClick={displayCalendarModal} className="text-sm underline flex items-center gap-1.5">
            <Calendar />
            {"Add dates"}
          </button>
        </div>
      ),
    },
    inStockItems: {
      title: "Browse in-stock only",
      annotation: "Only items available now with subscription",
      ctaContent: availabilityToggle,
    },
    allItems: {
      title: "Browse all items",
      annotation: "All items including currently out with members",
      ctaContent: availabilityToggle,
    },
    comingSoon: {
      title: "Browse coming soon",
      annotation: "Items available to rent soon",
      ctaContent: availabilityToggle,
    },
  } as const;

  const getFilterContent = () => {
    const { dateSelected, noDateSelected, inStockItems, allItems, comingSoon } = filterContent;
    if (isComingSoonGrid) {
      return comingSoon;
    } else if (rentalType === "reserve") {
      return date ? dateSelected : noDateSelected;
    } else {
      return isAvailableOnly ? inStockItems : allItems;
    }
  };

  const renderFilterView = () => {
    const { title, annotation, ctaContent } = getFilterContent();
    const classes = classNames("flex items-center justify-between", { "pt-4": !isComingSoonGrid });
    return (
      <div className={classes}>
        <div>
          <p className="text-md">{title}</p>
          <p className="text-sm text-darkGray">{annotation}</p>
        </div>
        {ctaContent}
      </div>
    );
  };

  return (
    <div className="rounded-xl bg-white p-4 text-lg flex flex-col">
      {!isComingSoonGrid && (
        <div className="border-b border-lighterGray w-full flex flex-col gap-2 pb-4">
          <div>When do you want to rent?</div>
          {renderRentalTypePills()}
        </div>
      )}
      {renderFilterView()}
    </div>
  );
};

export default GridFilterModalAvailabilitySection;
